import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isDefined, isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import PQueue from 'p-queue';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { selectDataByCurrencyPair } from '@/api/config/selectors.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { useValidateAlgoMutation } from '@/store/orders/algoOrders/api/index.ts';
import { selectAlgoTile } from '@/store/orders/algoOrders/selectors.ts';
import type {
  AlgoOrder,
  AlgoOrderRequest,
  FalconOrderProperties,
  NightjarOrderProperties,
  TwapOrderProperties,
} from '@/store/orders/algoOrders/state.ts';
import type { AlgoOrderType, ValidateOrderResponse } from '@/store/orders/state.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';

type AlgoValidateResult =
  | {
      data: ValidateOrderResponse;
      error: undefined;
    }
  | {
      data: undefined;
      error: FetchBaseQueryError | SerializedError;
    }
  | undefined;

type AlgoValidateFn = (
  updatedProperties: Partial<AlgoOrder['properties']>,
  newOrderType?: AlgoOrderType,
) => Promise<AlgoValidateResult>;

export type AlgoContextContent = {
  tileId: string;
  isChecking: boolean;
  isModifyingField: boolean;
  setIsModifyingField: (isModifying: boolean) => void;
  validateAlgo: AlgoValidateFn;
};

type AlgoModifyingFieldContextContent = {
  isModifyingField: boolean;
  setIsModifyingField: (isModifying: boolean) => void;
};

const AlgoTileIdContext = createContext('');

const AlgoIsCheckingContext = createContext(false);

const AlgoValidateContext = createContext<AlgoValidateFn>(async (_updatedProperties) => undefined);

const AlgoModifyingFieldContext = createContext<AlgoModifyingFieldContextContent>({
  isModifyingField: false,
  setIsModifyingField: () => {},
});

export const AlgoContext = createContext<AlgoContextContent>({
  tileId: '',
  isChecking: false,
  validateAlgo: async (_updatedProperties: Partial<AlgoOrder['properties']>, _newOrderType?: AlgoOrderType) =>
    undefined,
  isModifyingField: false,
  setIsModifyingField: () => {},
});

export const AlgoProvider = (props: { tileId: string; children: ReactNode }) => {
  const [isChecking, setIsChecking] = useState(false);
  const [isModifyingField, setIsModifyingField] = useState(false);

  const { children, tileId } = props;
  const [validateAlgoMutation] = useValidateAlgoMutation({
    selectFromResult: () => ({}),
  });
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));

  const { tile, preferencesNotificationEmails } = useAppSelector((state) => {
    const userPreferences = state.userPreferences;

    if (isNotDefined(userPreferences)) {
      console.error('[useValidateAlgo] User preferences not found in state.');
      return {};
    }

    const tile = selectAlgoTile(state, tileId);

    if (isNotDefined(tile)) {
      return {};
    }

    if (tile.type !== 'Twap+Order' && tile.type !== 'FalconOrder' && tile.type !== 'NightjarOrder') {
      return {};
    }

    return {
      tile,
      preferencesNotificationEmails: userPreferences.splitNotifications
        ? userPreferences.splitNotificationsEmailsOrders
        : userPreferences.emails,
    };
  }, deepEqual);

  // Queue with concurrency=1 to ensure sequential execution
  // Only one handleValidateTakeProfit request can run at a time
  const queue = useMemo(() => new PQueue({ concurrency: 1 }), []);

  const dataByCurrencyPair = useAppSelector(selectDataByCurrencyPair);

  const handleValidateAlgo = useCallback(
    async (updatedProperties: Partial<AlgoOrder['properties']>, newOrderType?: AlgoOrderType) => {
      return queue.add(async () => {
        if (isNotDefined(tile)) {
          console.error(`[handleValidateAlgo] Tile with id ${tileId} not found in state.`);
          return null;
        }

        const currency1 = updatedProperties.currencyPair?.currency1 ?? tile.properties.currencyPair?.currency1;
        const currency2 = updatedProperties.currencyPair?.currency2 ?? tile.properties.currencyPair?.currency2;

        if (isNotDefined(clientId)) {
          console.error('[handleValidateAlgo] client id is not defined');
          return null;
        }

        if (isNotDefined(currency1) || isNotDefined(currency2)) {
          console.error(`[handleValidateAlgo] Invalid currency pair: ${currency1}/${currency2}`);
          return null;
        }

        const isNotDeliverableCurrencyPair =
          dataByCurrencyPair.data?.dict[`${currency1}/${currency2}`]?.notDeliverable ?? false;

        const updatedAlgo = {
          ...tile,
          properties: {
            ...tile.properties,
            ...updatedProperties,
          },
        };

        const effectiveType = newOrderType ?? updatedAlgo.type;

        const patchedKeys = Object.keys(updatedProperties);
        const keysWhichShouldMakeLimitPriceReset = ['currencyPair', 'clientWay'];
        const shouldResetLimitPrice = keysWhichShouldMakeLimitPriceReset.some((key) => patchedKeys.includes(key));

        const notificationEmails = updatedAlgo.properties.notificationEmails ?? preferencesNotificationEmails ?? [];

        const checkTimer = setTimeout(() => {
          setIsChecking(true);
        }, 500);

        const TwapProperties =
          effectiveType === 'Twap+Order'
            ? {
                clippingMode: (updatedAlgo.properties as TwapOrderProperties).clippingMode ?? 'Automatic',
                spreadCapture: (updatedAlgo.properties as TwapOrderProperties).spreadCapture ?? false,
                clipSize: (updatedAlgo.properties as TwapOrderProperties).clipSize,
                randomize: (updatedAlgo.properties as TwapOrderProperties).randomize ?? false,
              }
            : undefined;

        const NightjarProperties =
          effectiveType === 'NightjarOrder'
            ? {
                speed: (updatedAlgo.properties as NightjarOrderProperties).speed ?? 'Normal',
                alphaSeeker: (updatedAlgo.properties as NightjarOrderProperties).alphaSeeker ?? false,
              }
            : undefined;

        const FalconProperties =
          effectiveType === 'FalconOrder'
            ? {
                speed: (updatedAlgo.properties as Partial<FalconOrderProperties>).speed ?? 'Normal',
              }
            : undefined;

        const data = await validateAlgoMutation({
          orderType: effectiveType,
          tileId,
          currency1,
          currency2,
          clientId,
          way: updatedAlgo.properties.clientWay,
          notional: updatedAlgo.properties.notional,
          notionalCurrencyIndex: updatedAlgo.properties.notionalCurrencyIndex,
          limitPrice: shouldResetLimitPrice ? undefined : updatedAlgo.properties.limitPrice,
          isNdf: isNotDeliverableCurrencyPair,
          ...TwapProperties,
          ...NightjarProperties,
          ...FalconProperties,
          liquidityPool: updatedAlgo.properties.liquidityPool,
          tenor: updatedAlgo.properties.tenor,
          expiryDate: updatedAlgo.properties.expiryDate,
          startDate: updatedAlgo.properties.startDate,
          notificationEmails: {
            isOverridenEmails: isDefined(updatedProperties.notificationEmails),
            emails: notificationEmails,
          },
          clockOffset: updatedAlgo.properties.clockOffset,
        } as AlgoOrderRequest);

        clearTimeout(checkTimer);
        setIsChecking(false);
        return data;
      });
    },
    [clientId, preferencesNotificationEmails, queue, tile, tileId, validateAlgoMutation, dataByCurrencyPair],
  );

  const validateAlgo = useCallback<AlgoValidateFn>(
    async (updatedProperties, newOrderType) => {
      const result = await handleValidateAlgo(updatedProperties, newOrderType);

      if (isNotDefined(result)) {
        console.error('[validate] Validation result is undefined');
        return undefined;
      }

      if ('data' in result && result.data !== undefined) {
        return { data: result.data, error: undefined };
      }

      if ('error' in result && result.error !== undefined) {
        return { data: undefined, error: result.error };
      }

      return undefined;
    },
    [handleValidateAlgo],
  );

  const modifyingFieldContent = useMemo(
    () => ({
      isModifyingField,
      setIsModifyingField,
    }),
    [isModifyingField],
  );

  const content = useMemo(
    () => ({
      tileId,
      isChecking,
      validateAlgo,
      ...modifyingFieldContent,
    }),
    [isChecking, modifyingFieldContent, tileId, validateAlgo],
  );

  return (
    <AlgoTileIdContext.Provider value={tileId}>
      <AlgoIsCheckingContext.Provider value={isChecking}>
        <AlgoValidateContext.Provider value={validateAlgo}>
          <AlgoModifyingFieldContext.Provider value={modifyingFieldContent}>
            <AlgoContext.Provider value={content}>{children}</AlgoContext.Provider>
          </AlgoModifyingFieldContext.Provider>
        </AlgoValidateContext.Provider>
      </AlgoIsCheckingContext.Provider>
    </AlgoTileIdContext.Provider>
  );
};

export function useAlgoTileId() {
  const tileId = useContext(AlgoTileIdContext);

  if (isNotDefined(tileId)) {
    throw new Error('Could not find tileId');
  }

  return tileId;
}

export function useAlgoIsChecking() {
  return useContext(AlgoIsCheckingContext);
}

export function useValidateAlgo() {
  return useContext(AlgoValidateContext);
}

export function useAlgoIsModifyingField() {
  return useContext(AlgoModifyingFieldContext).isModifyingField;
}

export function useAlgoSetIsModifyingField() {
  return useContext(AlgoModifyingFieldContext).setIsModifyingField;
}
